import { useState } from "react";

import { Alert } from "../base/Alert";
import { Button } from "../base/Button";
import Card from "../shared/Card/Card";
import LoadingWheel from "../shared/LoadingWheel";
import Modal from "../../layouts/Modal";
import { errorResolver } from "../../utils/errorResolver";
import { useClipboardCopy } from "../../hooks/useClipboardCopy";
import useNotification from "../shared/Notifications/useNotifications";
import useCustomerSendingDomains from "../../data/CustomerSendingDomain/useCustomerSendingDomains";
import useCustomerSendingDomainMutations from "../../data/CustomerSendingDomain/useCustomerSendingDomainMutations";
import { ISendingDomain } from "../../models/CustomerSendingDomain/CustomerSendingDomain";

interface CustomerSendingDomainListProps {
  customerId: string;
}

export function CustomerSendingDomainList({
  customerId,
}: CustomerSendingDomainListProps) {
  const { sendingDomains } = useCustomerSendingDomains(customerId);
  const { add, verify, remove } = useCustomerSendingDomainMutations(customerId);
  const { CopyToClipboardButton } = useClipboardCopy();
  const { addNotification } = useNotification();
  const [domain, setDomain] = useState("");
  const [pendingRemove, setPendingRemove] = useState<ISendingDomain | null>(null);

  const handleAdd = () => {
    const value = domain.trim();
    if (!value) return;

    add.mutate(
      { domain: value },
      {
        onSuccess: () => setDomain(""),
      }
    );
  };

  const handleVerify = (id: string) => {
    verify.mutate(id, {
      onSuccess: (res) => {
        addNotification({
          variant: res.data?.verified ? "success" : "warn",
          primaryText: res.message,
        });
      },
      onError: (err: unknown) => {
        addNotification({
          variant: "error",
          primaryText: errorResolver(err),
        });
      },
    });
  };

  const rows = sendingDomains.data ?? [];

  return (
    <>
      <Card title="Sending domains" bodyClassName="p-4">
        {sendingDomains.isLoading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : sendingDomains.isError ? (
          <Alert
            severity="error"
            title="Error loading sending domains"
            message={errorResolver(sendingDomains.error)}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Add a customer domain, copy the CNAME records into their DNS, then
              verify. Sending is ready once DKIM and return-path are verified.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                  }}
                  placeholder="example.com"
                  className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500"
                />
              </div>
              <Button
                onClick={handleAdd}
                disabled={!domain.trim() || add.isPending}
              >
                Add domain
              </Button>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-gray-500">No sending domains yet.</p>
            ) : (
              <div className="space-y-4">
                {rows.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-md border border-gray-200 p-3 space-y-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {row.domain}
                        </p>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            row.verified
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {row.verified ? "Verified" : "Pending"}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleVerify(row.id)}
                          disabled={verify.isPending}
                        >
                          Verify
                        </Button>
                        <Button
                          size="sm"
                          variant="outlined"
                          onClick={() => setPendingRemove(row)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                    {row.dnsRecords?.length ? (
                      <table className="min-w-full text-left text-sm">
                        <thead>
                          <tr className="text-xs font-medium uppercase text-gray-500">
                            <th className="py-1 pr-3">Purpose</th>
                            <th className="py-1 pr-3">Type</th>
                            <th className="py-1 pr-3">Host</th>
                            <th className="py-1 pr-3">Value</th>
                            <th className="py-1">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.dnsRecords.map((record) => (
                            <tr key={`${row.id}-${record.purpose}-${record.host}`}>
                              <td className="py-1 pr-3 text-gray-700">
                                {record.purpose}
                              </td>
                              <td className="py-1 pr-3 text-gray-700">
                                {record.type}
                              </td>
                              <td className="py-1 pr-3">
                                <div className="flex items-center gap-1 font-mono text-xs">
                                  <span>{record.host}</span>
                                  <CopyToClipboardButton copyText={record.host} />
                                </div>
                              </td>
                              <td className="py-1 pr-3">
                                <div className="flex items-center gap-1 font-mono text-xs">
                                  <span>{record.value}</span>
                                  <CopyToClipboardButton copyText={record.value} />
                                </div>
                              </td>
                              <td className="py-1 text-xs text-gray-700">
                                {record.verified
                                  ? "Verified"
                                  : record.status || "Pending"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal
        open={!!pendingRemove}
        setOpen={(open) => {
          if (!open) setPendingRemove(null);
        }}
        width="!w-1/3"
      >
        <div>
          <h2 className="text-lg font-medium leading-6 mb-4 text-gray-900">
            Remove sending domain
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Remove {pendingRemove?.domain}? Mail from this domain will no longer
            be authenticated until it is added again.
          </p>
          <div className="flex space-x-2">
            <Button
              onClick={() => {
                if (!pendingRemove) return;
                remove.mutate(pendingRemove.id, {
                  onSuccess: () => setPendingRemove(null),
                });
              }}
              disabled={remove.isPending}
            >
              Remove
            </Button>
            <Button
              variant="outlined"
              onClick={() => setPendingRemove(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
